import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Heart, ShoppingBag, Star } from 'lucide-react';
import { useCart } from '../../context/CartContext';
import { useWishlist } from '../../context/WishlistContext';
import toast from 'react-hot-toast';
import { cldImg } from '../../utils/cloudinary';
import { rememberAnchor } from '../../utils/scrollMemory';

export default function ProductCard({ product, badge, rowId }) {
  const location = useLocation();
  const { addToCart, setDrawerOpen, activeDeals } = useCart();
  const { isWishlisted, toggleWishlist } = useWishlist();
  const [adding, setAdding] = useState(false);
  const wishlisted = isWishlisted(product?.id);

  if (!product) return null;

  const activeDeal = activeDeals?.find(d => d.product_id === product.id);

  // Pick the chosen thumbnail among the product's own (non-variant) images.
  // The listing API can return images in DB order, so we must honour
  // is_thumbnail / sort_order here rather than trusting array position —
  // otherwise reordering images in admin won't update the card.
  const parentImages = (product.images || []).filter(img => !img.variant_id);
  const parentImage = parentImages.find(img => img.is_thumbnail)
    || parentImages.slice().sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0))[0];
  const firstVariantId = product.variants?.[0]?.id;
  const variant1Image = product.images?.find(img => img.variant_id === firstVariantId);
  const image = (parentImage || variant1Image || product.images?.[0])?.image_url;
  const variant = product.variants?.[0];
  const sellingPrice = parseFloat(variant?.discount_price) > 0 ? parseFloat(variant?.discount_price) : parseFloat(variant?.price) || 0;
  const mrpPrice = parseFloat(variant?.price) || 0;
  const price = activeDeal ? parseFloat(activeDeal.deal_price) : sellingPrice;
  const originalPrice = activeDeal
    ? (sellingPrice > price ? sellingPrice : (mrpPrice > price ? mrpPrice : null))
    : (mrpPrice > sellingPrice ? mrpPrice : null);

  const isSoldOut = product.variants?.length > 0 && product.variants.every(v => (v.stock ?? 0) === 0);

  // Anchor for scroll restoration. The same product can appear in several rows
  // (New Arrivals + Best Sellers + …), so we scope the anchor by its row — that
  // way Back returns to the exact card in the exact section the user clicked,
  // not just the first copy in the DOM.
  const pcard = rowId != null ? `${rowId}:${product.id}` : `${product.id}`;

  const handleAddToCart = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    setAdding(true);
    addToCart(product, variant, 1, activeDeal ? parseFloat(activeDeal.deal_price) : null);
    setTimeout(() => {
      setAdding(false);
      if (window.innerWidth >= 768) {
        setDrawerOpen(true);
      } else {
        toast.success('Added to cart!', { icon: '🛒', duration: 1500 });
      }
    }, 300);
  };

  const handleWishlist = (e) => {
    e.preventDefault();
    e.stopPropagation();
    toggleWishlist(product.id);
  };

  return (
    <Link to={`/product/${product.slug}`} className="group block"
      data-pcard={pcard}
      onClick={(e) => rememberAnchor(location.key, pcard, e.currentTarget.getBoundingClientRect().top + window.scrollY)}>
      <div className="card-hover bg-white rounded-2xl overflow-hidden border border-gray-100">
        {/* Image */}
        <div className="relative aspect-square overflow-hidden bg-gray-50">
          {image ? (
            <img src={cldImg(image, 400)} alt={product.name} loading="lazy" decoding="async"
              className={`w-full h-full object-cover group-hover:scale-105 transition-transform duration-500 ${isSoldOut ? 'grayscale' : ''}`} />
          ) : (
            <div className="w-full h-full flex items-center justify-center bg-brand-cream-light">
              <span className="text-4xl">📦</span>
            </div>
          )}

          {/* Badges */}
          <div className="absolute top-2 left-2 flex flex-col items-start gap-1">
            {!isSoldOut && activeDeal && (
              <span className="text-[10px] font-bold px-2 py-0.5 rounded" style={{ backgroundColor: '#e11d48', color: '#fff' }}>Deal</span>
            )}
            {!isSoldOut && !activeDeal && (badge === 'best-seller' || product.is_best_seller) && (
              <span className="text-[10px] font-bold px-2 py-0.5 rounded" style={{ backgroundColor: '#ff6b35', color: '#fff' }}>Best Seller</span>
            )}
            {!isSoldOut && !activeDeal && (badge === 'new-arrival' || product.is_featured) && badge !== 'best-seller' && !product.is_best_seller && (
              <span className="text-[10px] font-bold px-2 py-0.5 rounded" style={{ backgroundColor: '#22c55e', color: '#fff' }}>New Arrival</span>
            )}
            {!isSoldOut && !activeDeal && badge === 'clearance' && (
              <span className="text-[10px] font-bold px-2 py-0.5 rounded" style={{ backgroundColor: '#dc2626', color: '#fff' }}>Stock Clearance</span>
            )}
            {isSoldOut && (
              <span className="text-[10px] font-bold px-2 py-0.5 rounded bg-gray-800 text-white">Sold Out</span>
            )}
          </div>

          {/* Wishlist */}
          <button onClick={handleWishlist}
            className="absolute top-2 right-2 w-8 h-8 bg-white rounded-full shadow flex items-center justify-center transition-all hover:scale-110 active:scale-95">
            <Heart className={`w-4 h-4 transition-colors ${wishlisted ? 'fill-brand-red text-brand-red' : 'text-gray-400'}`} />
          </button>

          {/* Add to cart — hidden when sold out */}
          {!isSoldOut && (
            <button onClick={handleAddToCart} disabled={adding}
              className="absolute bottom-0 left-0 right-0 bg-brand-yellow text-brand-dark text-xs font-bold py-2.5 flex items-center justify-center gap-1.5
                translate-y-full group-hover:translate-y-0 transition-transform duration-300 md:flex sm:translate-y-0 md:translate-y-full">
              <ShoppingBag className="w-3.5 h-3.5" />
              {adding ? 'Adding...' : 'Add to Cart'}
            </button>
          )}
        </div>

        {/* Info */}
        <div className="p-3">
          <p className="text-xs text-gray-400 mb-0.5">{product.category?.name}</p>
          <h3 className="text-sm font-semibold text-brand-dark leading-tight line-clamp-2 mb-1.5">
            {product.name}
          </h3>

          {/* Rating */}
          {product.rating_avg > 0 && (
            <div className="flex items-center gap-1 mb-1.5">
              <Star className="w-3 h-3 fill-brand-yellow text-brand-yellow" />
              <span className="text-xs text-gray-600 font-medium">{Number(product.rating_avg).toFixed(1)}</span>
              <span className="text-xs text-gray-400">({product.rating_count || 0})</span>
            </div>
          )}

          {/* Price */}
          <div className="flex items-center gap-2">
            <span className="text-base font-bold text-brand-dark">₹{price}</span>
            {originalPrice && originalPrice > price && (
              <span className="text-xs text-gray-400 line-through">₹{originalPrice}</span>
            )}
          </div>
        </div>
      </div>
    </Link>
  );
}
